import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

interface RevenueData {
  date: string;
  totalRevenue: number;
  nftSales: number;
  royalties: number;
  streaming: number;
}

interface RevenueChartProps {
  data: RevenueData[];
  chartType?: 'line' | 'area' | 'bar';
}

const RevenueChart: React.FC<RevenueChartProps> = ({ data, chartType = 'area' }) => {
  const colors = {
    totalRevenue: '#3B82F6',
    nftSales: '#10B981',
    royalties: '#F59E0B',
    streaming: '#EF4444'
  };

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="date" 
              stroke="#6B7280"
              fontSize={12}
            />
            <YAxis 
              stroke="#6B7280"
              fontSize={12}
              tickFormatter={(value) => `$${value}`}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
              formatter={(value: number, name: string) => [`$${value.toFixed(2)}`, name]}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="totalRevenue" 
              stroke={colors.totalRevenue}
              strokeWidth={3}
              dot={{ fill: colors.totalRevenue, strokeWidth: 2, r: 4 }}
              name="总收益"
            />
            <Line 
              type="monotone" 
              dataKey="nftSales" 
              stroke={colors.nftSales}
              strokeWidth={2}
              dot={{ fill: colors.nftSales, strokeWidth: 2, r: 3 }}
              name="NFT销售"
            />
            <Line 
              type="monotone" 
              dataKey="royalties" 
              stroke={colors.royalties}
              strokeWidth={2}
              dot={{ fill: colors.royalties, strokeWidth: 2, r: 3 }}
              name="版税收入"
            />
            <Line 
              type="monotone" 
              dataKey="streaming" 
              stroke={colors.streaming}
              strokeWidth={2}
              dot={{ fill: colors.streaming, strokeWidth: 2, r: 3 }}
              name="流媒体收入"
            />
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="totalRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors.totalRevenue} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={colors.totalRevenue} stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="nftSales" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors.nftSales} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={colors.nftSales} stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="royalties" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors.royalties} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={colors.royalties} stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="streaming" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors.streaming} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={colors.streaming} stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="date" 
              stroke="#6B7280"
              fontSize={12}
            />
            <YAxis 
              stroke="#6B7280"
              fontSize={12}
              tickFormatter={(value) => `$${value}`}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
              formatter={(value: number, name: string) => [`$${value.toFixed(2)}`, name]}
            />
            <Legend />
            <Area 
              type="monotone" 
              dataKey="totalRevenue" 
              stroke={colors.totalRevenue}
              fillOpacity={1}
              fill="url(#totalRevenue)"
              strokeWidth={2}
              name="总收益"
            />
            <Area 
              type="monotone" 
              dataKey="nftSales" 
              stroke={colors.nftSales}
              fillOpacity={1}
              fill="url(#nftSales)"
              strokeWidth={2}
              name="NFT销售"
            />
            <Area 
              type="monotone" 
              dataKey="royalties" 
              stroke={colors.royalties}
              fillOpacity={1}
              fill="url(#royalties)"
              strokeWidth={2}
              name="版税收入"
            />
            <Area 
              type="monotone" 
              dataKey="streaming" 
              stroke={colors.streaming}
              fillOpacity={1}
              fill="url(#streaming)"
              strokeWidth={2}
              name="流媒体收入"
            />
          </AreaChart>
        );

      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="date" 
              stroke="#6B7280"
              fontSize={12}
            />
            <YAxis 
              stroke="#6B7280"
              fontSize={12}
              tickFormatter={(value) => `$${value}`}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
              formatter={(value: number, name: string) => [`$${value.toFixed(2)}`, name]}
            />
            <Legend />
            <Bar dataKey="nftSales" fill={colors.nftSales} name="NFT销售" radius={[2, 2, 0, 0]} />
            <Bar dataKey="royalties" fill={colors.royalties} name="版税收入" radius={[2, 2, 0, 0]} />
            <Bar dataKey="streaming" fill={colors.streaming} name="流媒体收入" radius={[2, 2, 0, 0]} />
          </BarChart>
        );

      default:
        return <div>不支持的图表类型</div>;
    }
  };

  return (
    <div className="w-full h-80">
      <ResponsiveContainer width="100%" height="100%">
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
};

// 收益分布饼图组件
export const RevenueDistributionChart: React.FC<{ data: any[] }> = ({ data }) => {
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  return (
    <div className="w-full h-80">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(1)}%`}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip 
            formatter={(value: number) => [`$${value.toFixed(2)}`, '收益']}
          />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RevenueChart;
